import { APP_LANGUAGES, getLanguageOptionLabel } from "../../utils/languages";
import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  RefreshCcw,
  Users,
  Mail,
  Phone,
  MapPin,
  Building,
  UserCheck,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Key,
  Lock,
  Eye,
  EyeOff,
  Trash2,
  Globe,
  CheckCircle,
  Plus,
  UserPlus,
  Edit2,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";
import { api } from "../../services/api";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";

type User = {
  _id: string;
  username?: string;
  email?: string;
  mobileNo?: string;
  country?: string;
  companyName?: string;
  city?: string;
  state?: string;
  roleId?: number;
  isActive?: boolean;
  createdAt?: string;
  region?: string;
  preferredLanguage?: string;
  dob?: string;
  dob_time?: string;
  dob_place?: string;
  allRegionsPending?: boolean;
  allRegionsApproved?: boolean;
};

type ApiResponse = {
  success: boolean;
  total: number;
  data: User[];
  error?: string;
};

const REGION_OPTIONS = [
  { value: "healjai", label: "Thailand" },
  { value: "indonesia", label: "Indonesia" },
  { value: "canada", label: "Canada" },
  { value: "uk", label: "UK" },
  { value: "gcc", label: "GCC" },
  { value: "malaysia", label: "Malaysia" },
  { value: "singapore", label: "Singapore" },
  { value: "philippines", label: "Philippines" },
  { value: "vietnam", label: "Vietnam" },
  { value: "mexico", label: "Mexico" },
  { value: "brazil", label: "Brazil" },
  { value: "korea", label: "Korea" },
  { value: "india", label: "India" },
  { value: "japan", label: "Japan" },
  { value: "spanish", label: "Spanish" },
  { value: "us", label: "US" },
];

const LANGUAGE_OPTIONS = APP_LANGUAGES.map((language) => ({
  value: language.code,
  label: getLanguageOptionLabel(language),
}));

const CreateUserModal = ({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    mobileNo: "",
    roleId: "2",
    preferredLanguage: "th",
    region: "healjai",
    dob: "",
    dob_time: "",
    dob_place: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.username.trim()) {
      toast.error("Username is required");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!formData.password.trim()) {
      toast.error("Password is required");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/backend/user/create", {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        mobileNo: formData.mobileNo || "",
        roleId: Number(formData.roleId),
        preferredLanguage: formData.preferredLanguage,
        region: formData.region,
        dob: formData.dob || "",
        dob_time: formData.dob_time || "",
        dob_place: formData.dob_place || "",
      });

      if (!response.data.userId) {
        throw new Error(response.data.error || "Failed to create user");
      }

      toast.success("User created successfully");
      onSuccess();
      onClose();
      setFormData({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        mobileNo: "",
        roleId: "2",
        preferredLanguage: "th",
        region: "healjai",
        dob: "",
        dob_time: "",
        dob_place: "",
      });
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to create user",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl sticky top-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-white" />
              <h3 className="text-lg font-bold text-white">Create New User</h3>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition p-1 rounded-lg hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-indigo-100 text-sm mt-1">
            Create a new user or tester account
          </p>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username *
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => handleChange("username", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
              placeholder="Enter username"
              autoFocus
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
              placeholder="user@example.com"
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile (Optional)
            </label>
            <input
              type="tel"
              value={formData.mobileNo}
              onChange={(e) => handleChange("mobileNo", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
              placeholder="+1 234 567 8900"
            />
          </div>

          {/* Region */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Region *
            </label>
            <select
              value={formData.region}
              onChange={(e) => handleChange("region", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all appearance-none cursor-pointer bg-white"
            >
              {REGION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Preferred Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preferred Language *
            </label>
            <select
              value={formData.preferredLanguage}
              onChange={(e) =>
                handleChange("preferredLanguage", e.target.value)
              }
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all appearance-none cursor-pointer bg-white"
            >
              {LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Role Dropdown - User / Tester only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              value={formData.roleId}
              onChange={(e) => handleChange("roleId", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all appearance-none cursor-pointer bg-white"
            >
              <option value="2">User</option>
              <option value="3">Tester</option>
            </select>
            {formData.roleId === "3" && (
              <p className="text-xs text-amber-600 mt-1">
                Tester accounts have restricted settings and cannot modify their
                profile.
              </p>
            )}
          </div>

          {/* DOB - Optional for testers */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DOB (Optional)
              </label>
              <input
                type="text"
                value={formData.dob}
                onChange={(e) => handleChange("dob", e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                placeholder="DD/MM/YYYY"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time (Optional)
              </label>
              <input
                type="text"
                value={formData.dob_time}
                onChange={(e) => handleChange("dob_time", e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                placeholder="HH:MM"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Place (Optional)
              </label>
              <input
                type="text"
                value={formData.dob_place}
                onChange={(e) => handleChange("dob_place", e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                placeholder="City, Country"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password *
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) =>
                  handleChange("confirmPassword", e.target.value)
                }
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                placeholder="Confirm password"
              />
            </div>
            {formData.confirmPassword &&
              formData.password !== formData.confirmPassword && (
                <p className="text-xs text-red-500 mt-1">
                  Passwords do not match
                </p>
              )}
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <RefreshCcw className="w-4 h-4 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4" />
              )}
              Create User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditUserModal = ({
  isOpen,
  onClose,
  user,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onSuccess: () => void;
}) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    mobileNo: "",
    preferredLanguage: "th",
    region: "healjai",
    roleId: "2",
    dob: "",
    dob_time: "",
    dob_place: "",
  });
  const [loading, setLoading] = useState(false);

  // Pre-fill form when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || "",
        email: user.email || "",
        mobileNo: user.mobileNo || "",
        preferredLanguage: user.preferredLanguage || "th",
        region: user.region || "healjai",
        roleId: String(user.roleId || 2),
        dob: user.dob || "",
        dob_time: (user as any).dob_time || "",
        dob_place: (user as any).dob_place || "",
      });
    }
  }, [user]);

  if (!isOpen) return null;

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.username.trim()) {
      toast.error("Username is required");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }

    setLoading(true);
    try {
      const response = await api.put(`/backend/user/${user?._id}`, {
        username: formData.username,
        email: formData.email,
        mobileNo: formData.mobileNo || "",
        preferredLanguage: formData.preferredLanguage,
        region: formData.region,
        roleId: Number(formData.roleId),
        dob: formData.dob || "",
        dob_time: formData.dob_time || "",
        dob_place: formData.dob_place || "",
      });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to update user");
      }

      toast.success("User updated successfully");
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to update user",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl sticky top-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-white" />
              <h3 className="text-lg font-bold text-white">Edit User</h3>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition p-1 rounded-lg hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-indigo-100 text-sm mt-1">
            Update user details for {user?.username || user?.email}
          </p>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username *
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => handleChange("username", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
              placeholder="Enter username"
              autoFocus
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
              placeholder="user@example.com"
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile (Optional)
            </label>
            <input
              type="tel"
              value={formData.mobileNo}
              onChange={(e) => handleChange("mobileNo", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
              placeholder="+1 234 567 8900"
            />
          </div>

          {/* Region */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Region *
            </label>
            <select
              value={formData.region}
              onChange={(e) => handleChange("region", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all appearance-none cursor-pointer bg-white"
            >
              {REGION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Preferred Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preferred Language *
            </label>
            <select
              value={formData.preferredLanguage}
              onChange={(e) =>
                handleChange("preferredLanguage", e.target.value)
              }
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all appearance-none cursor-pointer bg-white"
            >
              {LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Role Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              value={formData.roleId}
              onChange={(e) => handleChange("roleId", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all appearance-none cursor-pointer bg-white"
            >
              <option value="2">User</option>
              <option value="3">Tester</option>
            </select>
            {formData.roleId === "3" && (
              <p className="text-xs text-amber-600 mt-1">
                Tester accounts have restricted settings.
              </p>
            )}
          </div>

          {/* DOB Fields */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DOB (Optional)
              </label>
              <input
                type="text"
                value={formData.dob}
                onChange={(e) => handleChange("dob", e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                placeholder="DD/MM/YYYY"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time (Optional)
              </label>
              <input
                type="text"
                value={formData.dob_time}
                onChange={(e) => handleChange("dob_time", e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                placeholder="HH:MM"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Place (Optional)
              </label>
              <input
                type="text"
                value={formData.dob_place}
                onChange={(e) => handleChange("dob_place", e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                placeholder="City, Country"
              />
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <RefreshCcw className="w-4 h-4 animate-spin" />
              ) : (
                <Edit2 className="w-4 h-4" />
              )}
              Update User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UsersTableSkeleton: React.FC = () => {
  return (
    <tbody className="bg-white divide-y divide-gray-100">
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td className="px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-200" />
              <div className="space-y-2">
                <div className="h-4 w-32 rounded bg-gray-200" />
                <div className="h-3 w-48 rounded bg-gray-100" />
              </div>
            </div>
          </td>
          <td className="px-6 py-4">
            <div className="h-3 w-40 rounded bg-gray-200" />
          </td>
          <td className="px-6 py-4">
            <div className="h-3 w-28 rounded bg-gray-200" />
          </td>
          <td className="px-6 py-4">
            <div className="h-3 w-24 rounded bg-gray-100" />
          </td>
          <td className="px-6 py-4">
            <div className="h-6 w-20 rounded bg-gray-200" />
          </td>
          <td className="px-6 py-4 text-right">
            <div className="flex items-center justify-end gap-2">
              <div className="h-9 w-28 rounded-lg bg-gray-200" />
              <div className="h-9 w-9 rounded-lg bg-gray-200" />
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  );
};

const roleLabel = (roleId?: number) => {
  if (roleId === 1) return { label: "Admin", color: "purple" };
  if (roleId === 2) return { label: "User", color: "blue" };
  if (roleId === 3) return { label: "Tester", color: "amber" };
  return { label: roleId != null ? `Role ${roleId}` : "-", color: "gray" };
};

const ResetPasswordModal = ({
  isOpen,
  onClose,
  userName,
  userId,
  onReset,
}: {
  isOpen: boolean;
  onClose: () => void;
  userName?: string;
  userId: string;
  onReset: () => void;
}) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword.trim()) {
      toast.error("Please enter a new password");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);
    try {
      const response = await api.put(`/backend/user/${userId}`, {
        password: newPassword,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to reset password");
      }

      toast.success(`Password reset successfully for ${userName || "user"}`);
      onReset();
      onClose();
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      toast.error(error.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-white" />
              <h3 className="text-lg font-bold text-white">Reset Password</h3>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition p-1 rounded-lg hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-indigo-100 text-sm mt-1">
            Reset password for {userName || "user"}
          </p>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                placeholder="Enter new password"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                placeholder="Confirm new password"
              />
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              Password must be at least 6 characters long. The user will need to
              use this new password for their next login.
            </p>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <RefreshCcw className="w-4 h-4 animate-spin" />
              ) : (
                <Key className="w-4 h-4" />
              )}
              Reset Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserManager: React.FC = () => {
  const { user } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState("");
  const [roleId, setRoleId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [resetPasswordModal, setResetPasswordModal] = useState<{
    isOpen: boolean;
    userId: string;
    userName?: string;
  }>({ isOpen: false, userId: "" });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [editUserModal, setEditUserModal] = useState<{
    isOpen: boolean;
    user: User | null;
  }>({ isOpen: false, user: null });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (roleId) params.set("roleId", roleId);
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
    return params.toString();
  }, [roleId, debouncedSearch]);

  const fetchUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const url = `/backend/users${queryString ? `?${queryString}` : ""}`;
      const response = await api.get(url);

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to fetch users");
      }

      setUsers(Array.isArray(response.data.data) ? response.data.data : []);
      setTotal(
        typeof response.data.total === "number" ? response.data.total : 0,
      );
    } catch (e: any) {
      setError(e?.message || "Something went wrong");
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [queryString]);

  const clearFilters = () => {
    setSearch("");
    setRoleId("");
    setDebouncedSearch("");
  };

  const getRoleColor = (roleId?: number) => {
    if (roleId === 1) return "bg-purple-100 text-purple-700 border-purple-200";
    if (roleId === 2) return "bg-blue-100 text-blue-700 border-blue-200";
    if (roleId === 3) return "bg-amber-100 text-amber-700 border-amber-200";
    return "bg-gray-100 text-gray-600 border-gray-200";
  };

  const handleResetPassword = (userId: string, userName?: string) => {
    setResetPasswordModal({
      isOpen: true,
      userId,
      userName,
    });
  };

  const handlePasswordResetComplete = () => {
    // Refresh user list after password reset
    fetchUsers();
  };

  const handleDeleteClick = (targetUser: User) => {
    setUserToDelete(targetUser);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      const response = await api.delete(`/backend/user/${userToDelete._id}`);
      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to delete user");
      }
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (e: any) {
      toast.error(e?.message || "Failed to delete user");
    } finally {
      setDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const handleApproveAllRegions = async (targetUser: User) => {
    try {
      await api.put(`/backend/user/${targetUser._id}`, {
        approveAllRegions: true,
      });
      toast.success(
        `All Regions approved for ${targetUser.username || targetUser.email}`,
      );
      fetchUsers();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to approve All Regions");
    }
  };

  return (
    <div className="bg-white">
      {/* Stats Cards */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-6 border-b border-gray-200">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{total}</p>
              <p className="text-xs text-gray-600">Total Users</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter((u) => u.roleId === 1).length}
              </p>
              <p className="text-xs text-gray-600">Administrators</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter((u) => u.roleId === 2).length}
              </p>
              <p className="text-xs text-gray-600">Regular Users</p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Error Display */}
      {error && (
        <div className="mx-6 mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Header with Create User Button */}
      <div className="px-6 pt-6 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">All Users</h3>
          <p className="text-sm text-gray-500">
            Manage user accounts and roles
          </p>
        </div>
        <button
          onClick={() => setCreateUserModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium hover:shadow-lg transition-all text-sm"
        >
          <UserPlus className="w-4 h-4" />
          Create User
        </button>
      </div>

      {/* Users Table */}
      <div className="p-6">
        {users.length === 0 && !loading ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500">No users found</p>
            {(search || roleId) && (
              <button
                onClick={clearFilters}
                className="mt-2 text-sm text-indigo-600 hover:text-indigo-700"
              >
                Clear filters to see all users
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    User
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Mobile No
                  </th>
                  {/* <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Company
                  </th> */}
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Region
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              {loading ? (
                <UsersTableSkeleton />
              ) : (
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((u) => {
                    const role = roleLabel(u.roleId);
                    const roleColorClass = getRoleColor(u.roleId);

                    return (
                      <tr
                        key={u._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-xl flex items-center justify-center ${roleColorClass}`}
                            >
                              <span className="text-sm font-semibold">
                                {u.username?.charAt(0).toUpperCase() || "U"}
                              </span>
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900">
                                {u.username || "Unnamed User"}
                              </div>
                              {(u.city || u.state || u.country) && (
                                <div className="text-xs text-gray-500">
                                  {[u.city, u.state, u.country]
                                    .filter(Boolean)
                                    .join(", ")}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {u.email ? (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Mail className="w-3.5 h-3.5 text-gray-400" />
                              <span className="truncate">{u.email}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {u.mobileNo ? (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Phone className="w-3.5 h-3.5 text-gray-400" />
                              <span>{u.mobileNo}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        {/* <td className="px-6 py-4">
                          {u.companyName ? (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Building className="w-3.5 h-3.5 text-gray-400" />
                              <span className="truncate">
                                {u.companyName}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td> */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${roleColorClass}`}
                          >
                            {role.label}
                          </span>
                        </td>
                        {/* Region column */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {u.region ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-indigo-200 bg-indigo-50 text-indigo-700">
                              <Globe className="w-3 h-3" />
                              {u.allRegionsApproved ? "All Regions" : u.region}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                          {u.allRegionsPending && !u.allRegionsApproved && (
                            <button
                              onClick={() => handleApproveAllRegions(u)}
                              className="ml-2 h-7 px-2 rounded-lg border border-green-200 bg-green-50 hover:bg-green-100 transition inline-flex items-center gap-1 text-xs text-green-700"
                              title="Approve All Regions"
                            >
                              <CheckCircle className="w-3 h-3" />
                              Approve
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2 flex-nowrap">
                            <button
                              onClick={() =>
                                handleResetPassword(u._id, u.username)
                              }
                              className="h-9 w-9 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 transition inline-flex items-center justify-center"
                              title="Reset Password"
                            >
                              <Key className="w-4 h-4 text-amber-700" />
                            </button>
                            <button
                              onClick={() =>
                                setEditUserModal({ isOpen: true, user: u })
                              }
                              className="h-9 w-9 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition inline-flex items-center justify-center"
                              title="Edit User"
                            >
                              <Edit2 className="w-4 h-4 text-indigo-700" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(u)}
                              className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-red-50 transition inline-flex items-center justify-center"
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
        )}
      </div>

      {/* Footer with Refresh */}
      {!loading && users.length > 0 && (
        <div className="px-6 pb-6">
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Showing {users.length} of {total} users
            </p>
            <button
              onClick={fetchUsers}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition"
            >
              <RefreshCcw
                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={resetPasswordModal.isOpen}
        onClose={() => setResetPasswordModal({ isOpen: false, userId: "" })}
        userName={resetPasswordModal.userName}
        userId={resetPasswordModal.userId}
        onReset={handlePasswordResetComplete}
      />

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message="Are you sure you want to delete this user?"
        itemName={userToDelete?.username || userToDelete?.email}
      />

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={createUserModalOpen}
        onClose={() => setCreateUserModalOpen(false)}
        onSuccess={fetchUsers}
      />

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={editUserModal.isOpen}
        onClose={() => setEditUserModal({ isOpen: false, user: null })}
        user={editUserModal.user}
        onSuccess={fetchUsers}
      />
    </div>
  );
};

export default UserManager;
